import React, { useMemo } from "react";
import { TableContainer, TableStyled } from "../Returns.styled";
import { LoadingIndicator } from "../../../components/LoadingIndicator/LoadingIndicator";
import { Text } from "@sede-enterprise/shell-ds-react-framework";
import { ISourceAuction } from "../../../services";
import { CellInfo } from "../../../core/types";
import { formatDeliveryPeriod, formatNumberWithCommas } from "../utils";

interface TableProps {
  rowData: ISourceAuction[];
  isLoading: boolean;
  maxHeight: number;
  maxWidth: number;
  renderQuantityInput: (auctionId: string) => React.ReactNode;
  renderEditButton: (name: string) => React.ReactNode;
  tableHeight?: number; // Added prop for dynamic table height
}

/**
 * Renders a text value in a table cell
 */
const renderTextValue = (value: any) => {
  return <div>{value}</div>;
};

/**
 * Generates column definitions for the source auctions table
 */
export const getSourceAuctionColumns = (
  renderQuantityInput: (auctionId: string) => React.ReactNode,
  renderEditButton: (name: string) => React.ReactNode
) => [
  {
    header: "Source Auction",
    dataIndex: "auctionName",
    accessorKey: "auctionName",
    width: 220,
    minWidth: 220,
    maxWidth: 220,
    cell: (info: CellInfo) => renderTextValue(info.getValue()),
  },
  {
    header: "Delivery From / To",
    dataIndex: "deliveryPeriod",
    accessorKey: "deliveryPeriod",
    width: 180,
    minWidth: 180,
    maxWidth: 180,
    cell: (info: CellInfo) =>
      renderTextValue(
        formatDeliveryPeriod(
          info.row.original.deliveryPeriodStart,
          info.row.original.deliveryPeriodEnd
        )
      ),
  },
  {
    header: "Capacity (MW)",
    dataIndex: "availableAllocatedCapacity",
    accessorKey: "availableAllocatedCapacity",
    width: 160,
    minWidth: 160,
    maxWidth: 160,
    cell: (info: CellInfo) =>
      renderTextValue(formatNumberWithCommas(info.getValue())),
  },
  {
    header: "Clearing Price",
    dataIndex: "clearingPrice",
    accessorKey: "clearingPrice",
    width: 60,
    minWidth: 60,
    maxWidth: 60,
    cell: (info: CellInfo) => renderTextValue(info.getValue()),
  },
  {
    header: "Qty to Return (MW)",
    dataIndex: "auctionId",
    accessorKey: "auctionId",
    width: 60,
    minWidth: 60,
    maxWidth: 60,
    cell: (info: CellInfo) => renderQuantityInput(info.getValue() as string),
  },
  {
    header: "",
    dataIndex: "name",
    accessorKey: "name",
    width: 20,
    minWidth: 20,
    maxWidth: 20,
    cell: (info: CellInfo) => renderEditButton(info.getValue() as string),
  },
];

/**
 * Component for the table that displays source auctions
 */
export const SourceAuctionsTable: React.FC<TableProps> = ({
  rowData,
  isLoading,
  maxHeight,
  maxWidth,
  renderQuantityInput,
  renderEditButton,
  tableHeight, // Use the dynamic table height if provided
}) => {
  const hasSourceAuctions = rowData && rowData.length > 0;

  // Calculate appropriate table height based on data or use provided tableHeight
  const calculatedTableHeight = useMemo(() => {
    const baseRowHeight = 48; // Each row height
    const headerHeight = 48; // Table header height
    const minTableHeight = 150; // Minimum table height for UI consistency

    if (tableHeight) {
      return tableHeight; // Use provided height if available
    }

    if (hasSourceAuctions) {
      // Calculate based on number of rows plus header
      const calculatedHeight = (rowData.length + 1) * baseRowHeight;
      return Math.max(calculatedHeight, minTableHeight);
    }

    return minTableHeight; // Default minimum height
  }, [rowData, hasSourceAuctions, tableHeight]);

  const renderTable = () => {
    return (
      <TableStyled
        data-testid="returns-table"
        data={rowData}
        columns={getSourceAuctionColumns(renderQuantityInput, renderEditButton)}
        size="small"
        stickyHeader={true}
        maxHeight={maxHeight}
        maxWidth={maxWidth}
      />
    );
  };

  const renderNoSourceAuctions = () => {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
        }}
      >
        <Text bold={true}>
          No source auctions available for capacity return
        </Text>
      </div>
    );
  };

  return (
    <TableContainer dynamicHeight={calculatedTableHeight}>
      {isLoading ? (
        <LoadingIndicator />
      ) : hasSourceAuctions ? (
        renderTable()
      ) : (
        renderNoSourceAuctions()
      )}
    </TableContainer>
  );
};